import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import boTheme from '@ecp/common/src/style/theme/bo';
import { FlexBox, FlexBoxColumn, FlexBoxSpaceBetween, VerticalDivider } from '@ecp/common/src/layouts/flex/styled';
import { Button } from '@ecp/common/src/components/button/bo/Button';
import TreeUnstyled from '@ecp/common/src/components/unstyled/tree/TreeUnstyled';
import TreeNode from '@ecp/common/src/components/tree/bo/TreeNode';
import defaultUnchecked from '@ecp/common/src/assets/icon/checkbox__bo--unchecked.svg';
import defaultChecked from '@ecp/common/src/assets/icon/checkbox__bo--checked.svg';
import { ReactComponent as ExpandIcon } from '@ecp/common/src/assets/icon/icon__bo__tree-expand.svg';
import { ReactComponent as CollapseIcon } from '@ecp/common/src/assets/icon/icon__bo__tree-collapse.svg';

const StyledRoot = styled(FlexBoxColumn)`
  align-items: flex-start;
  color: ${boTheme.color.sub1};
  font-size: ${boTheme.font.size.small};
  gap: 10px;
`;

const TreeSummary = styled(FlexBox)`
  height: 28px;
  line-height: 28px;

  & span {
    font-weight: bold;
  }
`;

const StyledButton = (props) => <Button type={'Third'} padding={'0 8px'} width={'unsest'} {...props} />;

const StyledDefaultRoot = ({
  children,
  showCheckedCount,
  checkedCount,
  expandCheckedButtonText,
  addButtonText,
  onExpandAll,
  onCollapseAll,
  onExpandChecked,
  onAdd,
  customButtonText,
  onCustomButtonClick,
  ...props
}) => {
  return (
    <StyledRoot {...props}>
      <FlexBoxSpaceBetween width={'100%'}>
        <FlexBox gap={'6px'}>
          <StyledButton onClick={onExpandAll}>모두 펼치기</StyledButton>
          <StyledButton onClick={onCollapseAll}>모두 접기</StyledButton>
          {customButtonText && (
            <>
              <VerticalDivider height={'12px'} />
              <StyledButton onClick={onCustomButtonClick}>{customButtonText}</StyledButton>
            </>
          )}
        </FlexBox>
        <FlexBox gap={'6px'}>
          {expandCheckedButtonText && <StyledButton onClick={onExpandChecked}>{expandCheckedButtonText}</StyledButton>}
          {addButtonText && <StyledButton onClick={onAdd}>{addButtonText}</StyledButton>}
        </FlexBox>
      </FlexBoxSpaceBetween>
      {showCheckedCount && (
        <TreeSummary>
          총&nbsp;<span>{checkedCount}</span>개 선택
        </TreeSummary>
      )}
      {children}
    </StyledRoot>
  );
};

StyledDefaultRoot.propTypes = {
  children: PropTypes.node,
  showCheckedCount: PropTypes.bool,
  checkedCount: PropTypes.number,
  expandCheckedButtonText: PropTypes.string,
  addButtonText: PropTypes.string,
  onExpandAll: PropTypes.func,
  onCollapseAll: PropTypes.func,
  onExpandChecked: PropTypes.func,
  onAdd: PropTypes.func,
  customButtonText: PropTypes.string,
  onCustomButtonClick: PropTypes.func,
};

StyledDefaultRoot.defaultProps = {
  showCheckedCount: false,
};

const rootStyle = css`
  &:first-child {
    height: 44px;
    padding-bottom: 14px;
    border-bottom: 1px solid ${boTheme.color.sub6};
    font-size: ${boTheme.font.size.medium};
    font-weight: ${boTheme.font.weight.bold};

    .rc-tree-checkbox {
      display: none;
    }

    & .rc-tree-node-content-wrapper {
      &:hover {
        background-color: unset;
      }
    }
  }

  & .rc-tree-indent {
    display: flex;
    align-self: stretch;
    height: unset;

    & .rc-tree-indent-unit {
      width: 26px;
      height: 100%;
      position: relative;

      &:not(:first-child):after {
        position: absolute;
        display: block;
        content: '';
        width: 1px;
        height: calc(100% + 8px);
        left: 50%;
        top: -4px;
        background: ${boTheme.color.sub6};
      }
    }
  }
`;

const rootlessStyle = css`
  & .rc-tree-indent {
    display: flex;
    align-self: stretch;
    height: unset;

    & .rc-tree-indent-unit {
      width: 26px;
      height: 100%;
      position: relative;

      &:after {
        position: absolute;
        display: block;
        content: '';
        width: 1px;
        height: calc(100% + 8px);
        left: 50%;
        top: -4px;
        background: ${boTheme.color.sub6};
      }
    }
  }
`;

const StyledTree = styled.div`
  width: 100%;
  height: 100%;
  padding: 14px;
  border: 1px solid ${boTheme.color.sub6};
  overflow: auto;

  ::-webkit-scrollbar-thumb {
    min-height: 15px;
  }
  .rc-tree {
    & .rc-tree-list-holder-inner {
      gap: 8px;
    }

    & .rc-tree-treenode {
      min-height: 28px;
      display: flex;
      align-items: flex-start;

      & > span {
        line-height: 0;
        flex-shrink: 0;
      }

      ${({ rootless }) => (rootless ? rootlessStyle : rootStyle)}
      & .rc-tree-switcher {
        width: 20px;
        height: 20px;
        margin: 4px 3px;
        background: unset;
      }

      & .rc-tree-checkbox {
        width: 20px;
        height: 20px;
        margin: 4px 3px;
        background-image: url(${defaultUnchecked});
        background-size: ${({ width }) => width || '20px'};
        background-position: unset;
      }

      & .rc-tree-checkbox-indeterminate,
      & .rc-tree-checkbox-checked {
        background-image: url(${defaultChecked});
      }

      & .rc-tree-node-content-wrapper {
        color: ${boTheme.color.sub1};
        height: unset;
        margin-left: 3px;
      }

      & .rc-tree-node-selected {
        color: ${boTheme.color.main};
        background: unset;
        box-shadow: unset;
        opacity: unset;
      }

      & .rc-tree-iconEle {
        width: unset;
        height: unset;
        display: flex;
        margin: unset;
      }

      & .rc-tree-title {
        display: none;
      }

      &.rc-tree-treenode-draggable {
        border-left: unset;

        &.drop-target {
          background-color: #f2f1ff;
        }

        &.dragging {
          background: unset;
        }
      }
    }
  }
`;

const Tree = (props) => {
  return (
    <TreeUnstyled
      rootComponent={StyledDefaultRoot}
      wrapperComponent={StyledTree}
      treeNodeComponent={TreeNode}
      switcherIcon={(obj) => {
        return obj.isLeaf || obj.expanded ? <CollapseIcon /> : <ExpandIcon />;
      }}
      {...props}
    />
  );
};

Tree.displayName = 'Tree';

Tree.propTypes = {
  data: PropTypes.array,
};

Tree.defaultProps = {
  data: [],
  onChange: PropTypes.func,
  onDelete: PropTypes.func,
  onAdd: PropTypes.func,
  onUpdate: PropTypes.func,
};

export default Tree;
